import React from 'react';
import styled from 'styled-components';
import axios from 'axios';
import errorReporter from '@app/errorReporter';
import Swal from 'sweetalert2';
import { Input } from 'antd';
import { BasicContact, OfferSettingsJsonType } from '@bb/domain/dist/types';
import { FroalaTaggedInput } from 'components';
import { replaceMarkupWithTags, replaceTagsWithMarkup } from 'utils';
import Modal, {
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../react-common/Modal';
import { Loading, ExpansionPanel, ShowingAgentsFilter } from '../react-common';
import type { Coop } from '../offer/OfferRegistrationModal';

export interface ManageOfferNotificationModalListing {
  id: string;
  line1: string;
  line2?: string;
}

interface ManageOfferNotificationModalProps {
  listing: ManageOfferNotificationModalListing;
  show: boolean;
  closeModal: (requireReload?: boolean) => void;
  isListingAgent?: boolean;
}

type RecipientType = 'all' | 'none' | 'registered' | 'selected';

interface ManageOfferNotificationModalState {
  isLoading: boolean;
  agentsPanelExpanded: boolean;
  isProcessing: boolean;
  instructions: string;
  selectedRecipients: RecipientType;
  agents: Coop[];
  initialAgentsCopy: Coop[];
  message?: string;
  defaultMessage: string;
  subject?: string;
  defaultSubject: string;
}

const GROUP_LABELS: Record<
  RecipientType,
  {
    title: string;
    subtitle: string;
    option: string;
    button: string;
  }
> = {
  all: {
    title: 'Notifications Sent',
    subtitle: 'Notification has been sent to all Showing Agents.',
    option: 'All Showing Agents',
    button: 'Send Notification',
  },
  registered: {
    title: 'Notifications Sent',
    subtitle:
      'Notification has been sent to all Agents with registered offers.',
    option: 'Agents with Registered Offers',
    button: 'Send Notification',
  },
  none: {
    title: 'Offer Confirmed',
    subtitle:
      'The offer has now been registered and no parties have been notified based on your selection.',
    option: "Don't notify anyone",
    button: 'Done',
  },
  selected: {
    title: 'Notifications Sent',
    subtitle: 'Notification has been sent to all selected Agents.',
    option: '',
    button: 'Send Notification',
  },
};

class ManageOfferNotificationModal extends React.Component<
  ManageOfferNotificationModalProps,
  ManageOfferNotificationModalState
> {
  constructor(props: ManageOfferNotificationModalProps) {
    super(props);

    this.state = {
      isLoading: true,
      agentsPanelExpanded: false,
      isProcessing: false,
      instructions: '',
      selectedRecipients: 'all',
      agents: [],
      initialAgentsCopy: [],
      message: '',
      defaultMessage: '',
      subject: '',
      defaultSubject: '',
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    const changes = {
      ...(await this.fetchOfferAgents()),
      ...(await this.fetchOfferTemplate()),
    };
    this.setState({ ...this.state, isLoading: false, ...changes });
  };

  fetchOfferAgents = async () => {
    try {
      const { listing } = this.props;
      if (!listing.id) return {};

      const {
        data: { agentsSubscribed, agentsUnsubscribed },
      } = await axios.get(`/listing/${listing.id}/offer_agents`);

      const agents = [...agentsSubscribed, ...agentsUnsubscribed] as Coop[];

      const processedAgents = agents.map(agent => ({
          ...agent,
          checked: true,
          uid: agent.uid || agent.id,
        }));
      return {
        agents: processedAgents,
        initialAgentsCopy: processedAgents,
      };
    } catch (err) {
      errorReporter.error(`Error fetching offer agents: ${err}`);
      return {};
    }
  };

  fetchOfferTemplate = async () => {
    try {
      const { listing } = this.props;
      const {
        data: {
          offerSettings: { message },
          offerCount,
        },
      } = await axios.get<{
        offerSettings: OfferSettingsJsonType;
        offerCount: number;
      }>(`/listing/${listing.id}/offer_details`);

      const defaultSubject = `NEW REGISTERED OFFER (${offerCount} TOTAL) - ${
        listing.line1
      } ${listing.line2 || ''}`;

      const formattedMessage = replaceTagsWithMarkup(message);

      return {
        message: formattedMessage,
        defaultMessage: formattedMessage,
        subject: defaultSubject,
        defaultSubject,
      };
    } catch (err) {
      errorReporter.error(`Error fetching offer details: ${err}`);
      return {};
    }
  };

  onInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ instructions: e.target.value });
  };

  onRecipientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedRecipients = e.target.value as RecipientType;
    const { initialAgentsCopy } = this.state;

    if (selectedRecipients === 'all') {
      this.setState({
        selectedRecipients,
        agents: initialAgentsCopy.map(agent => ({ ...agent, checked: true })),
      });
      return;
    }
    if (selectedRecipients === 'none') {
      this.setState({
        selectedRecipients,
        agentsPanelExpanded: false,
        agents: initialAgentsCopy.map(agent => ({ ...agent, checked: false })),
      });
      return;
    }
    if (selectedRecipients === 'registered') {
      this.setState({
        selectedRecipients,
        agents: initialAgentsCopy
          .filter(({ hasRegisteredOffer }) => hasRegisteredOffer)
          .map(agent => ({
            ...agent,
            checked: Boolean(agent.hasRegisteredOffer),
          })),
      });
    }
  };

  onChangeAgentsPanel = (expanded: boolean): void => {
    const { selectedRecipients } = this.state;
    let newValue = expanded;
    if (selectedRecipients === 'none') {
      newValue = false;
    }
    this.setState({ agentsPanelExpanded: newValue });
  };

  onSubjectChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const subject = e.target.value;
    this.setState({ subject });
  };

  onFroalaChange = (message: string): void => {
    this.setState({ message });
  };

  renderCount = (option: string) => {
    const { selectedRecipients, agents } = this.state;
    if (option !== selectedRecipients) return '';

    const result = {
      total: selectedRecipients === 'all' ? agents.length : 0,
      selected: 0,
    };
    agents.forEach(({ checked, hasRegisteredOffer }) => {
      if (checked) {
        result.selected += 1;
      }
      if (selectedRecipients === 'registered') {
        if (hasRegisteredOffer) {
          result.total += 1;
        }
      }
    });
    return `(${result.selected}/${result.total})`;
  };

  sendNotification = async () => {
    const { listing, closeModal } = this.props;
    const {
      selectedRecipients,
      instructions,
      message,
      agents,
      defaultMessage,
      subject,
      defaultSubject,
    } = this.state;
    if (!selectedRecipients) return;

    const sendNotificationUrl = `/listing/${listing.id}/offer/notify`;
    const messageOverride =
      message && defaultMessage.trim() !== message.trim()
        ? replaceMarkupWithTags(message)
        : null;
    const subjectOverride = defaultSubject !== subject ? subject : null;
    let agentGroup: RecipientType = 'selected';

    const selectedAgents = agents.filter(agent => agent.checked);

    const agentsWithOffer = agents.filter(agent => agent.hasRegisteredOffer);

    const shouldConvertToRegistered =
      selectedAgents.length === agentsWithOffer.length &&
      selectedAgents.every(agent => agent.hasRegisteredOffer);

    if (selectedRecipients === 'none') {
      // If selectedRecipients is none, notify only the LA regardless of the selection
      agentGroup = 'none';
    } else if (
      selectedRecipients === 'registered' &&
      shouldConvertToRegistered
    ) {
      // If selection count and offer agent count are both 0, shouldConvertToRegistered is true.
      agentGroup = 'registered';
    } else if (
      selectedRecipients === 'all' &&
      selectedAgents.length === agents.length
    ) {
      agentGroup = 'all';
    }

    const data: any = {
      instructions,
    };

    let agentsToNotify;

    if (agentGroup === 'selected') {
      agentsToNotify = agents
        .filter(({ checked }) => checked)
        .map(agent => {
          const newAgent = { ...agent };
          delete newAgent.checked;
          return newAgent;
        });
    }

    data.agentGroup = agentGroup;
    if (agentsToNotify) data.agentsToNotify = agentsToNotify;
    if (messageOverride) data.messageOverride = messageOverride;
    if (subjectOverride) data.subjectOverride = subjectOverride;

    try {
      this.setState({ isProcessing: true });
      await axios.post(sendNotificationUrl, data);
      Swal.fire({
        title: GROUP_LABELS[agentGroup].title,
        text: GROUP_LABELS[agentGroup].subtitle,
        type: 'success',
      });
    } catch (err) {
      Swal.fire({
        title: 'Error sending notifications',
        text:
          "Something happened and we weren't able to send the notifications " +
          'for this listing. Please try again.',
        type: 'error',
      });
    } finally {
      this.setState({ isProcessing: false }, () => closeModal(true));
    }
  };

  renderButtonLabel = () => {
    const { selectedRecipients, agents } = this.state;
    let agentGroup = selectedRecipients;

    const noAgentSelected = agents.every(({ checked }) => !checked);

    if (noAgentSelected) agentGroup = 'none';

    return GROUP_LABELS[agentGroup].button;
  };

  render() {
    const { listing, show, closeModal, isListingAgent } = this.props;
    const {
      isLoading,
      agentsPanelExpanded,
      isProcessing,
      instructions,
      message,
      selectedRecipients,
      agents,
      subject,
    } = this.state;
    return (
      <Modal
        data-cy="manage-offer-notification-modal"
        show={show}
        onHide={closeModal}
      >
        <ModalHeader>
          <ModalTitle data-cy="manage-offer-notification-modal-title">
            {listing.line1} — Manage Notifications
          </ModalTitle>
        </ModalHeader>
        <ModalBody style={{ maxHeight: '600px', overflow: 'auto' }}>
          {isLoading ? (
            <Loading
              data-cy="manage-offer-notification-modal-loading"
              size="fa-5x"
            />
          ) : (
            <>
              {!isListingAgent && (
                <section>
                  <label htmlFor="manage-offer-notification-instructions">
                    Please log who gave you instructions to manage notifications
                    on this offer registration:
                  </label>
                  <Input.TextArea
                    name="instructions"
                    id="manage-offer-notification-instructions"
                    cols={60}
                    rows={4}
                    value={instructions}
                    onChange={this.onInstructionsChange}
                    data-cy="manage-offer-notification-instructions"
                  />
                  {!instructions && (
                    <label className="error">
                      Notification description must be provided.
                    </label>
                  )}
                </section>
              )}
              <section className="m-t">
                <label htmlFor="manage-offer-notification-recipients">
                  Notify the following group regarding the number of offers
                  registered on this property:
                </label>
                <ExpansionPanel
                  data-cy="manage-offer-notification-modal-edit-recipients"
                  value={agentsPanelExpanded}
                  onChange={this.onChangeAgentsPanel}
                  buttonLabel="Edit Recipients"
                  title={<h5>Notification Group</h5>}
                  subtitle={
                    <div>
                      <select
                        value={selectedRecipients}
                        onChange={this.onRecipientChange}
                        style={{
                          fontSize: '15px',
                          borderColor: '#ddd',
                          backgroundColor: 'white',
                        }}
                        data-cy="offer-select-notify-group"
                      >
                        <option value="all">
                          {GROUP_LABELS.all.option} {this.renderCount('all')}
                        </option>
                        <option value="registered">
                          {GROUP_LABELS.registered.option}{' '}
                          {this.renderCount('registered')}
                        </option>
                        <option value="none">{GROUP_LABELS.none.option}</option>
                      </select>
                    </div>
                  }
                >
                  <ShowingAgentsFilter
                    data-cy="manage-offer-notification-modal-search-agents"
                    onChange={(newAgents: Coop[]) =>
                      this.setState({ agents: newAgents })
                    }
                    agents={agents}
                    extraFilter={({ hasRegisteredOffer }: any) =>
                      selectedRecipients === 'all' ||
                      (selectedRecipients === 'registered' &&
                        hasRegisteredOffer)
                    }
                  />
                </ExpansionPanel>
                <ExpansionPanel
                  data-cy="manage-offer-notification-modal-edit-template"
                  buttonLabel="Edit Template"
                  title={<h5>Notification Template</h5>}
                >
                  <label style={{ width: '100%', marginBottom: '20px' }}>
                    <div>Subject</div>
                    <Input
                      data-cy="manage-offer-notification-modal-notification-subjet-input"
                      value={subject}
                      onChange={this.onSubjectChange}
                      disabled
                    />
                  </label>

                  <label>Body</label>

                  <FroalaWrapper>
                    <FroalaTaggedInput
                      data-cy="manage-offer-notification-modal-notification-body-input"
                      model={message}
                      onModelChange={this.onFroalaChange}
                      templateType="offer"
                      config={{ toolbarSticky: false }}
                    />
                  </FroalaWrapper>
                </ExpansionPanel>
              </section>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-default"
            onClick={() => closeModal()}
            disabled={isProcessing}
            data-cy="manage-offer-notification-modal-cancel-notification"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn btn-success"
            onClick={this.sendNotification}
            disabled={
              isProcessing ||
              (!isListingAgent && !instructions) ||
              !subject.trim().length
            }
            data-cy="manage-offer-notification-modal-send-notification"
          >
            {this.renderButtonLabel()}
          </button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default ManageOfferNotificationModal;

const FroalaWrapper = styled.div`
  position: relative;
`;
